import { useNavigate } from "react-router-dom";
import { Layers, TrendingUp, Sparkles, Lock, DollarSign, ArrowRight } from "lucide-react";
import type { Post } from "../types";

interface PostCardProps {
  post: Post;
}

function stripCategoryPrefix(name: string): string {
  if (name.startsWith("auto_")) return name.substring(5);
  return name;
}

function formatDate(dateStr?: string): string {
  if (!dateStr) return "";
  return dateStr.slice(0, 10).replace(/-/g, "/");
}

function CategoryIcon({ name }: { name: string }) {
  const lower = name.toLowerCase();
  if (lower.includes("go") || lower.includes("语言")) return <TrendingUp className="w-3.5 h-3.5" />;
  if (lower.includes("react") || lower.includes("前端") || lower.includes("vue")) return <Sparkles className="w-3.5 h-3.5" />;
  if (lower.includes("ai") || lower.includes("智能")) return <Sparkles className="w-3.5 h-3.5" />;
  return <Layers className="w-3.5 h-3.5" />;
}

export function PostCard({ post }: PostCardProps) {
  const navigate = useNavigate();

  return (
    <article className="h-full group">
      <div
        role="link"
        tabIndex={0}
        onClick={() => navigate(`/post?id=${post.id}`)}
        onKeyDown={event => { if (event.target === event.currentTarget && event.key === "Enter") navigate(`/post?id=${post.id}`); }}
        className="relative h-full w-full text-left flex flex-col overflow-hidden rounded-xl border border-[var(--border-default)] bg-[var(--bg-surface)] transition-colors hover:border-[var(--border-strong)] hover:bg-[var(--bg-hover)] focus:outline-none focus:ring-2 focus:ring-[var(--text-primary)]/20"
      >
        <div className="relative flex flex-col h-full p-7 sm:p-8">
          {/* Meta row */}
          <div className="flex flex-wrap items-center gap-2.5 mb-6">
            {post.category ? (
              <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-semibold bg-primary/10 text-primary-light border border-primary/20 transition-colors group-hover:bg-primary/15 group-hover:border-primary/30">
                <CategoryIcon name={post.category} />
                {stripCategoryPrefix(post.category)}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-semibold bg-[var(--bg-hover)] text-[var(--text-tertiary)] border border-[var(--border-default)]">
                <Layers className="w-3.5 h-3.5" />
                未分类
              </span>
            )}

            {post.pricingType === "paid" && (
              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-amber/10 text-amber border border-amber/20">
                <DollarSign className="w-3 h-3" />
                付费
              </span>
            )}

            {post.visibility === "private" && (
              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-[var(--border-subtle)] text-[var(--text-tertiary)] border border-[var(--border-default)]">
                <Lock className="w-3 h-3" />
                私有
              </span>
            )}
          </div>

          {/* Title */}
          <h3 className="text-xl sm:text-[1.375rem] font-bold leading-snug line-clamp-2 text-[var(--text-primary)] mb-4">
            {post.title}
          </h3>

          {/* Summary */}
          <p className="text-[15px] leading-[1.75] text-[var(--text-secondary)] line-clamp-3 mb-8 flex-1">
            {post.summary || "暂无摘要"}
          </p>

          {/* Tags */}
          {post.tags && post.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-6">
              {post.tags.slice(0, 3).map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center text-xs text-[var(--text-tertiary)] bg-[var(--bg-hover)] px-3 py-1.5 rounded-full border border-[var(--border-default)] transition-all duration-300 group-hover:border-[var(--border-strong)] group-hover:text-[var(--text-secondary)]"
                >
                  #{tag}
                </span>
              ))}
              {post.tags.length > 3 && (
                <span className="inline-flex items-center text-xs text-[var(--text-tertiary)] px-2 py-1.5">
                  +{post.tags.length - 3}
                </span>
              )}
            </div>
          )}

          {/* Footer */}
          <div className="flex items-center justify-between pt-6 mt-auto border-t border-[var(--border-subtle)]">
            <div className="flex items-center gap-3">
              {post.author ? (
                <>
                  <button type="button" onClick={event=>{event.stopPropagation();if(post.userId)navigate(`/users/${post.userId}`);}} className="inline-flex items-center gap-3 text-left group/author" aria-label={`查看 ${post.author} 的主页`}>
                    <span className="inline-flex items-center justify-center w-9 h-9 rounded-full overflow-hidden bg-gradient-to-br from-primary to-accent text-white text-sm font-bold shadow-md">{post.authorAvatar?<img src={post.authorAvatar} alt="" className="w-full h-full object-cover"/>:post.author.charAt(0).toUpperCase()}</span>
                    <span className="text-sm font-semibold text-[var(--text-primary)] group-hover/author:text-primary-light">{post.author}</span>
                  </button>
                  <div className="flex flex-col">
                    <span className="text-xs text-[var(--text-tertiary)]">{formatDate(post.createdAt)}</span>
                  </div>
                </>
              ) : (
                <span className="text-xs text-[var(--text-tertiary)]">{formatDate(post.createdAt)}</span>
              )}
            </div>

            <span className="flex items-center gap-1.5 text-sm font-semibold text-[var(--text-primary)]">
              阅读
              <ArrowRight className="w-4 h-4" />
            </span>
          </div>
        </div>
      </div>
    </article>
  );
}
